import React, { useCallback } from 'react';
import { PlugZap } from 'lucide-react';
import type { LoadsSection } from '@officehub/shared';
import { INPUT_CLASS, LABEL_CLASS, TEXTAREA_CLASS, TOGGLE_ACTIVE, TOGGLE_INACTIVE } from './styles';

export interface LoadsFormProps {
  data: LoadsSection;
  onUpdate: (d: Partial<LoadsSection>) => void;
}

const TRANSFER_OPTIONS: { value: LoadsSection['transferType']; label: string }[] = [
  { value: 'whole_house', label: 'Whole House' },
  { value: 'partial', label: 'Partial' },
  { value: 'critical_only', label: 'Critical Only' },
];

const CRITICAL_LOAD_PRESETS = [
  'HVAC',
  'Well Pump',
  'Kitchen',
  'Refrigerator',
  'Lighting',
  'Medical Equipment',
  'Pool/Spa',
  'Security',
  'Garage',
  'Other',
];

export function LoadsForm({ data, onUpdate }: LoadsFormProps): React.ReactElement {
  // A cleared optional input emits null, never undefined. survey_updateSection
  // merges the fields it is given (issue #3230), so only an explicit null
  // deletes a field — an absent key just leaves the stored value alone. The
  // callable SDK happens to encode undefined as null on the wire, but that is
  // an implementation detail of @firebase/functions; saying null here states
  // the intent at the boundary instead of depending on the transport for it.
  const handleChange = useCallback(
    (field: keyof LoadsSection, value: unknown) => {
      onUpdate({ [field]: value });
    },
    [onUpdate],
  );

  const toggleCriticalLoad = useCallback(
    (load: string) => {
      const current = data.criticalLoads ?? [];
      const updated = current.includes(load)
        ? current.filter((l) => l !== load)
        : [...current, load];
      onUpdate({ criticalLoads: updated });
    },
    [onUpdate, data.criticalLoads],
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Section header */}
      <div className="flex items-center gap-2 px-1">
        <PlugZap className="w-3.5 h-3.5 text-slate-400" />
        <h2 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.1em]">Loads</h2>
        <div className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent" />
      </div>

      {/* Transfer Type — 3 pill buttons */}
      <div>
        <label className={LABEL_CLASS}>Transfer Type</label>
        <div className="flex gap-2">
          {TRANSFER_OPTIONS.map(({ value, label }) => {
            const isActive = data.transferType === value;
            return (
              <button
                key={value}
                type="button"
                data-testid={`transfer-type-${value}`}
                onClick={() => handleChange('transferType', value)}
                className={[
                  'flex-1 py-2.5 text-xs rounded-xl transition-all duration-150 min-h-[48px]',
                  isActive ? TOGGLE_ACTIVE : TOGGLE_INACTIVE,
                ].join(' ')}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Critical Loads chip multi-picker */}
      <div>
        <label className={LABEL_CLASS}>Critical Loads</label>
        <div className="flex flex-wrap gap-2">
          {CRITICAL_LOAD_PRESETS.map((load) => {
            const isSelected = (data.criticalLoads ?? []).includes(load);
            return (
              <button
                key={load}
                type="button"
                data-testid={`critical-load-${load.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                onClick={() => toggleCriticalLoad(load)}
                className={[
                  'px-3 py-2 rounded-full text-xs font-bold transition-all duration-150 min-h-[36px]',
                  isSelected
                    ? 'bg-[#3FA5FF]/10 ring-1 ring-[#3FA5FF]/30 text-[#007ACC]'
                    : 'bg-slate-100 ring-1 ring-slate-200 text-slate-500',
                ].join(' ')}
              >
                {load}
              </button>
            );
          })}
        </div>
      </div>

      {/* Estimated Total Load kW */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={LABEL_CLASS}>Estimated Total Load (kW)</label>
          <input
            data-testid="field-estimatedTotalLoadKW"
            type="number"
            inputMode="decimal"
            className={INPUT_CLASS}
            value={data.estimatedTotalLoadKW ?? ''}
            onChange={(e) =>
              handleChange(
                'estimatedTotalLoadKW',
                e.target.value === '' ? null : Number(e.target.value),
              )
            }
            placeholder="kW"
          />
        </div>
      </div>

      {/* Customer Priorities — full width */}
      <div>
        <label className={LABEL_CLASS}>Customer Priorities</label>
        <textarea
          className={TEXTAREA_CLASS}
          rows={3}
          value={data.customerPriorities ?? ''}
          onChange={(e) => handleChange('customerPriorities', e.target.value)}
          placeholder="What does the customer consider most important to keep running…"
        />
      </div>

      {/* Future Expansion Notes — full width */}
      <div>
        <label className={LABEL_CLASS}>Future Expansion Notes</label>
        <textarea
          className={TEXTAREA_CLASS}
          rows={3}
          value={data.futureExpansionNotes ?? ''}
          onChange={(e) => handleChange('futureExpansionNotes', e.target.value)}
          placeholder="EV charger, pool, addition, solar battery plans…"
        />
      </div>
    </div>
  );
}
